import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { authClient } from '@/lib/auth-client';
import { api, ApiError } from '@/lib/api-client';
import { Sidebar } from '@/components/layout/sidebar';
import { MobileNav } from '@/components/layout/mobile-nav';
import { EmailVerificationBanner } from '@/components/auth/email-verification-banner';
import { NotificationBell } from '@/components/notifications/notification-bell';
// import { ChangelogModal } from '@/components/changelog/changelog-modal';

// Cache: stores verified user ID + timestamp. Expires after 5 min or on user change.
let verifiedUser: { id: string; at: number } | null = null;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/** Clear the onboarding verification cache. Must be called when switching workspaces. */
export function clearVerifiedUserCache() {
  verifiedUser = null;
}

export const Route = createFileRoute('/_authenticated')({
  beforeLoad: async () => {
    const session = await authClient.getSession();
    if (!session.data) {
      throw redirect({ to: '/' });
    }

    const currentUserId = session.data.user?.id;

    // Ensure activeOrganizationId is set (may be missing after fresh login).
    if (!session.data.session?.activeOrganizationId) {
      try {
        const orgs = await authClient.organization.list();
        if (orgs.data && orgs.data.length > 0) {
          await authClient.organization.setActive({ organizationId: orgs.data[0].id });
        }
      } catch {
        // Will be caught by onboarding check below
      }
    }

    // Use cache only if same user AND within TTL
    const cacheValid =
      verifiedUser && verifiedUser.id === currentUserId && Date.now() - verifiedUser.at < CACHE_TTL;

    if (!cacheValid) {
      let onboardingIncomplete = false;
      try {
        const result = await api.get<{
          data: { profileCompleted: boolean; workspaceCompleted: boolean; deactivated?: boolean };
        }>('/users/me/onboarding');
        if (result.data?.deactivated) {
          verifiedUser = null;
          throw redirect({ to: '/deactivated' });
        }
        if (result.data?.profileCompleted && result.data?.workspaceCompleted) {
          verifiedUser = { id: currentUserId!, at: Date.now() };
        } else {
          onboardingIncomplete = true;
        }
      } catch (e) {
        // Re-throw redirects (from deactivated check above or onboarding)
        if (e && typeof e === 'object' && 'to' in e) throw e;
        // API error (401, 400, network) — don't let user through
        if (e instanceof ApiError && e.status === 401) {
          throw redirect({ to: '/' });
        }
        // Any other API failure (e.g. "No active workspace") — treat as deactivated
        throw redirect({ to: '/deactivated' });
      }
      if (onboardingIncomplete) {
        throw redirect({ to: '/onboarding' });
      }
    }

    return { session: session.data };
  },
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  return (
    <div className="min-h-screen bg-background">
      {/* <ChangelogModal /> — disabled until content is finalized */}

      {/* Desktop Sidebar */}
      <Sidebar />

      {/* Mobile Navigation */}
      <MobileNav />

      {/* Main Content */}
      <main className="lg:pl-56">
        {/* Mobile spacer for fixed header */}
        <div className="h-14 lg:hidden" />

        {/* Sticky top bar — desktop only */}
        <div className="sticky top-0 z-30 hidden bg-background/95 backdrop-blur-sm lg:block">
          <div className="flex items-center gap-4 px-6 py-3">
            <div className="flex-1">
              <EmailVerificationBanner />
            </div>
            <div className="shrink-0">
              <NotificationBell />
            </div>
          </div>
        </div>

        <div className="p-6 pt-0 lg:pt-3">
          {/* Mobile: banner only (bell is in mobile nav) */}
          <div className="lg:hidden">
            <EmailVerificationBanner />
          </div>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
